import { Badge } from "@/components/ui/badge";
import { Bug, Sprout, FlaskConical, X, Microscope, AlertTriangle, ShieldAlert } from "lucide-react";

const sev = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-emerald-100 text-emerald-700 border-emerald-200",
};

const IssueCard = ({ item, protocolLabel }) => (
  <div data-testid={`grass-issue-${item.name}`} className="rounded-2xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
    <div className="grid grid-cols-2">
      <div className="relative">
        <img src={item.early_image} alt="early stage" className="h-40 w-full object-cover" />
        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-full"><Microscope className="w-3 h-3" /> Early Stage</span>
      </div>
      <div className="relative border-l-2 border-white">
        <img src={item.severe_image} alt="severe damage" className="h-40 w-full object-cover" />
        <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Severe Damage</span>
      </div>
    </div>
    <div className="p-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h4 className="font-serif text-xl text-emerald-950">{item.name}</h4>
        <Badge className={`rounded-full border ${sev[item.severity]}`}>{item.severity} risk</Badge>
      </div>
      <p className="mt-3 text-sm text-stone-600"><span className="font-semibold text-stone-800">Symptoms — </span>{item.symptoms_text}</p>
      <p className="mt-2 text-sm text-stone-600"><span className="font-semibold text-stone-800">{protocolLabel} — </span>{item.treatment_protocol}</p>
    </div>
  </div>
);

const Section = ({ title, icon: Icon, items, protocolLabel }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <h3 className="font-serif text-2xl text-emerald-950 flex items-center gap-2"><Icon className="w-5 h-5 text-emerald-700" /> {title}</h3>
      <div className="mt-3 grid gap-4">
        {items.map((it) => <IssueCard key={it.name} item={it} protocolLabel={protocolLabel} />)}
      </div>
    </div>
  );
};

export const GrassDetail = ({ grass, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-auto" onClick={onClose}>
    <div data-testid="grass-detail" onClick={(e) => e.stopPropagation()} className="bg-stone-50 rounded-3xl max-w-3xl w-full my-6 max-h-[90vh] overflow-auto p-7">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-3xl text-emerald-950">{grass.name}</h2>
          <p className="text-sm italic text-stone-500">{grass.scientific_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge data-testid="grass-overall-risk" className={`rounded-full border ${sev[grass.overall_risk]}`}>{grass.overall_risk} risk</Badge>
          <button data-testid="grass-detail-close" onClick={onClose} className="text-stone-400 hover:text-stone-700"><X className="w-5 h-5" /></button>
        </div>
      </div>

      {/* Field state pair */}
      <div className="mt-5 rounded-2xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
        <div className="grid grid-cols-2">
          <div className="relative">
            <img src={grass.healthy_image} alt="healthy" className="h-44 w-full object-cover" />
            <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-full"><Sprout className="w-3 h-3" /> Early Stage · Healthy</span>
          </div>
          <div className="relative border-l-2 border-white">
            <img src={grass.fungi[0]?.severe_image || grass.pests[0]?.severe_image} alt="severe" className="h-44 w-full object-cover" />
            <span className="absolute bottom-2 left-2 flex items-center gap-1 text-[10px] uppercase tracking-wider bg-black/60 text-white px-2 py-1 rounded-full"><AlertTriangle className="w-3 h-3" /> Severe Damage</span>
          </div>
        </div>
      </div>

      {/* ID Blueprint */}
      <div className="mt-5 rounded-2xl bg-emerald-50/60 border border-emerald-100 p-4">
        <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 flex items-center gap-2"><ShieldAlert className="w-4 h-4" /> ID Blueprint</p>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div><dt className="text-stone-400 text-xs">Vernation</dt><dd className="text-emerald-950 capitalize">{grass.identification.vernation}</dd></div>
          <div><dt className="text-stone-400 text-xs">Ligule</dt><dd className="text-emerald-950">{grass.identification.ligule_label}</dd></div>
          <div className="col-span-2"><dt className="text-stone-400 text-xs">Collar</dt><dd className="text-emerald-950">{grass.identification.collar}</dd></div>
          <div className="col-span-2"><dt className="text-stone-400 text-xs">Seedhead</dt><dd className="text-emerald-950">{grass.identification.seedhead_type}</dd></div>
        </dl>
      </div>

      <div className="mt-6 space-y-6">
        <Section title="Known Diseases &amp; Pathogens" icon={Microscope} items={grass.fungi} protocolLabel="Treatment protocol" />
        <Section title="Known Pests" icon={Bug} items={grass.pests} protocolLabel="Treatment protocol" />
        <Section title="Nutrient Deficiencies" icon={FlaskConical} items={grass.deficiencies} protocolLabel="Correction protocol" />
      </div>
    </div>
  </div>
);
